import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { token, user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
  }, []);

  const checkExistingSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      setSubscription(sub);
      setIsSubscribed(!!sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported]);

  const subscribeToPush = useCallback(async () => {
    if (!token) {
      console.log('No token, cannot subscribe');
      return null;
    }

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check for existing subscription
      let sub = await registration.pushManager.getSubscription();
      
      if (!sub) {
        // Get VAPID key from server
        let vapidKey;
        try {
          const { data } = await axios.get(`${API}/push/vapid-key`);
          vapidKey = data.publicKey;
        } catch (e) {
          // Fallback to default VAPID key
          vapidKey = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';
        }

        // Create new subscription
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }
      
      setSubscription(sub);
      setIsSubscribed(true);
      
      // Send subscription to server
      try {
        const subJson = sub.toJSON();
        await axios.post(`${API}/push/subscribe`, {
          endpoint: subJson.endpoint,
          keys: subJson.keys
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Push subscription saved to server');
      } catch (e) {
        console.error('Error saving subscription to server:', e);
      }
      
      return sub;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;

    setLoading(true);
    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();
      
      // Remove from server
      if (token) {
        try {
          const subJson = subscription.toJSON();
          await axios.post(`${API}/push/unsubscribe`, {
            endpoint: subJson.endpoint,
            keys: subJson.keys
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          console.error('Error removing subscription from server:', e);
        }
      }
      
      setSubscription(null);
      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription, token]);

  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (permission !== 'granted') {
      console.log('Notification permission not granted');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        vibrate: [100, 50, 100],
        ...options
      });
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }, [permission]);

  const sendTestNotification = useCallback(async () => {
    if (!token) return false;

    try {
      await axios.post(`${API}/push/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test notification sent!');
      return true;
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
      return false;
    }
  }, [token]);

  // Auto-subscribe when permission is granted and user is logged in
  useEffect(() => {
    if (user && permission === 'granted' && !isSubscribed && isSupported && token) {
      // Small delay for service worker to be ready
      const timer = setTimeout(() => {
        subscribeToPush();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, permission, isSubscribed, isSupported, token, subscribeToPush]);

  return {
    permission,
    subscription,
    isSupported,
    isSubscribed,
    loading,
    requestPermission,
    subscribeToPush,
    unsubscribe,
    showLocalNotification,
    sendTestNotification
  };
}

// Component to prompt for notifications
export function NotificationPrompt() {
  const { isSupported, permission, isSubscribed, requestPermission, subscribeToPush, loading } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);
  const { user } = useAuth();

  // Show prompt after a delay when user is logged in
  useEffect(() => {
    if (user && !isSubscribed && permission !== 'denied') {
      const timer = setTimeout(() => {
        setShow(true);
      }, 5000); // Show after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [user, isSubscribed, permission]);

  // Don't show if not supported or already subscribed
  if (!isSupported || isSubscribed || dismissed || !show) {
    return null;
  }

  // Don't show if permission already denied
  if (permission === 'denied') {
    return null;
  }

  const handleEnable = async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) {
        toast.error('Please enable notifications in your browser settings');
        setDismissed(true);
        return;
      }
    }
    
    const success = await subscribeToPush();
    if (success) {
      toast.success('Notifications enabled! 🔔');
      setDismissed(true);
    }
  };

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-gradient-to-r from-pink-500 to-violet-500 rounded-xl p-4 shadow-lg z-50 animate-in slide-in-from-bottom-5" data-testid="notification-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm">Stay Updated!</p>
          <p className="text-white/80 text-xs mt-1">
            Get notified about new messages, appointments, and more.
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleEnable}
          disabled={loading}
          className="flex-1 bg-white text-pink-500 font-semibold text-sm py-2 px-4 rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50"
          data-testid="enable-notifications-btn"
        >
          {loading ? 'Enabling...' : 'Enable'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="px-4 py-2 text-white/80 text-sm hover:text-white transition-colors"
          data-testid="dismiss-notifications-btn"
        >
          Later
        </button>
      </div>
    </div>
  );
}

export default usePushNotifications;
